import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Rpfi13PosGroup, Rpfi13TableResult } from '../../../../core/models/report/rpfi13.model';
import { MapperRpfi13Service } from '../../../../core/mappers/report/mapper-rpfi13.service';
import { ApiRpfi13Service } from '../../../../core/services/api/report/api-rpfi13.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-rpfi13',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './rpfi13.component.html',
  styleUrl: './rpfi13.component.css',
})
export class Rpfi13Component {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRpfi13Service);
  private readonly mapper = inject(MapperRpfi13Service);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'RPFI13.pdf';

  startDate = '';
  endDate = '';
  lastFetchedTime: Date | null = null;
  hasSearched = signal(false);

  private readonly maxRangeMonths = 2;
  result = signal<Rpfi13TableResult | null>(null);

  constructor() {
    const today = this.toInputDate(new Date());
    this.startDate = today;
    this.endDate = today;

    effect(() => {
      this.siteState.selectedSite()?.id;
      untracked(() => this.clearResult());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get groups(): Rpfi13PosGroup[] {
    return this.result()?.groups ?? [];
  }

  get totals() {
    return this.result()?.totals ?? null;
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  onFilterChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getRpfi13ForTable({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (res) => {
        this.result.set(this.mapper.mapTable(res));
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายงาน RPFI13 ไม่สำเร็จ');
      },
    });
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || this.groups.length === 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows: Record<string, string | number>[] = [];
      for (const g of this.groups) {
        for (const r of g.rows) {
          exportRows.push({
            'ลำดับ': r.seq,
            'จุดรับชำระเงิน': g.posName,
            'วันเวลา': this.formatDate(r.paidDate),
            'ใบกำกับภาษีเริ่มต้น': r.receiptStart,
            'ใบกำกับภาษีสิ้นสุด': r.receiptEnd,
            'จำนวน': r.count,
            'เงินสด': r.cash,
            'QR': r.qr,
            'เงินโอน': r.transfer,
            'Discount': r.discount,
            'อื่นๆ': r.other,
            'รวมทั้งสิ้น': r.total,
          });
        }
        exportRows.push({
          'ลำดับ': '',
          'จุดรับชำระเงิน': `ยอดรวม ${g.posName}`,
          'วันเวลา': '',
          'ใบกำกับภาษีเริ่มต้น': '',
          'ใบกำกับภาษีสิ้นสุด': '',
          'จำนวน': g.sumCount,
          'เงินสด': g.sumCash,
          'QR': g.sumQr,
          'เงินโอน': g.sumTransfer,
          'Discount': g.sumDiscount,
          'อื่นๆ': g.sumOther,
          'รวมทั้งสิ้น': g.sumTotal,
        });
      }
      const t = this.totals!;
      exportRows.push({
        'ลำดับ': '',
        'จุดรับชำระเงิน': `ยอดรวมทั้งสิ้น (${t.sumCount} รายการ)`,
        'วันเวลา': '',
        'ใบกำกับภาษีเริ่มต้น': '',
        'ใบกำกับภาษีสิ้นสุด': '',
        'จำนวน': t.sumCount,
        'เงินสด': t.sumCash,
        'QR': t.sumQr,
        'เงินโอน': t.sumTransfer,
        'Discount': t.sumDiscount,
        'อื่นๆ': t.sumOther,
        'รวมทั้งสิ้น': t.sumTotal,
      });

      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      await downloadReportExcel(exportRows, {
        reportTitle: 'RPFI13 — สรุปรายได้ แยกประเภทการรับชำระ',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: this.startDate,
        endDate: this.endDate,
        sheetName: 'RPFI13',
        fileName: buildReportExcelFilename('RPFI13', siteName, this.startDate, this.endDate),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและช่วงวันที่');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `RPFI13_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api.getRpfi13Pdf({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: (err) => {
        console.error('[API Error] getRpfi13Pdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
